import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../utils/api';

const roleBadge = (role) => ({
  admin:    'bg-red-100 text-red-700',
  farmer:   'bg-emerald-100 text-emerald-700',
  butcher:  'bg-amber-100 text-amber-700',
  customer: 'bg-blue-100 text-blue-700',
}[role] || 'bg-gray-100 text-gray-600');

// ── Pagination helpers (same pattern as PaymentsPanel) ────
function usePerPage() {
  const [perPage, setPerPage] = useState(5);
  const change = (n) => setPerPage(n);
  return { perPage, change };
}

function usePagination(data, perPage) {
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(data.length / perPage) || 1;
  const slice = data.slice((page - 1) * perPage, page * perPage);
  const reset = () => setPage(1);
  return { page, setPage, totalPages, slice, reset };
}

function PerPageSelector({ perPage, onChange }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-secondary">Rows</span>
      <div className="flex gap-1">
        {[5, 10, 20, 50].map(n => (
          <button key={n} onClick={() => onChange(n)}
            className={`w-8 h-7 rounded-lg text-xs font-bold transition-all
              ${perPage === n
                ? 'bg-primary text-white'
                : 'text-secondary hover:bg-surface-container border border-surface-container-high'}`}>
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

function PaginationBar({ page, totalPages, setPage }) {
  if (totalPages <= 1) return null;
  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= page - 1 && i <= page + 1)) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== '...') {
      pages.push('...');
    }
  }
  return (
    <div className="flex items-center justify-between mt-3">
      <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold
                   text-secondary hover:text-on-surface hover:bg-surface-container
                   disabled:opacity-30 disabled:cursor-not-allowed transition-all">
        <span className="material-symbols-outlined text-[14px]">chevron_left</span>
        Prev
      </button>
      <div className="flex items-center gap-1">
        {pages.map((p, i) => p === '...' ? (
          <span key={`dots-${i}`} className="px-1 text-xs text-secondary">…</span>
        ) : (
          <button key={p} onClick={() => setPage(p)}
            className={`w-7 h-7 rounded-lg text-xs font-bold transition-all
              ${page === p ? 'bg-primary text-white' : 'text-secondary hover:bg-surface-container'}`}>
            {p}
          </button>
        ))}
      </div>
      <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold
                   text-secondary hover:text-on-surface hover:bg-surface-container
                   disabled:opacity-30 disabled:cursor-not-allowed transition-all">
        Next
        <span className="material-symbols-outlined text-[14px]">chevron_right</span>
      </button>
    </div>
  );
}

// ── Confirm modal ─────────────────────────────────────────
function ConfirmModal({ user, action, onConfirm, onCancel }) {
  const isBan = action === 'ban';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl p-6 shadow-2xl border border-outline-variant/20 w-full max-w-sm mx-4">
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 ${isBan ? 'bg-amber-50' : 'bg-red-50'}`}>
          <span className={`material-symbols-outlined text-[24px] ${isBan ? 'text-amber-600' : 'text-red-600'}`}>
            {isBan ? 'block' : 'delete_forever'}
          </span>
        </div>
        <h3 className="font-black text-on-surface text-lg">{isBan ? 'Ban User?' : 'Delete User?'}</h3>
        <p className="text-sm text-secondary mt-1">
          {isBan
            ? `Ban "${user.name}" from accessing the platform?`
            : `Permanently delete "${user.name}"? This cannot be undone.`}
        </p>
        <div className="flex gap-3 mt-5">
          <button onClick={onCancel}
            className="flex-1 px-4 py-2.5 rounded-xl border border-outline-variant/30
                       text-sm font-semibold text-on-surface-variant hover:bg-surface-container transition-all">
            Cancel
          </button>
          <button onClick={onConfirm}
            className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-bold text-white transition-all
                        ${isBan ? 'bg-amber-500 hover:bg-amber-600' : 'bg-red-600 hover:bg-red-700'}`}>
            {isBan ? 'Yes, Ban' : 'Yes, Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── User row actions ──────────────────────────────────────
function UserActions({ user, onBan, onDelete }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = React.useRef(null);

  const handleOpen = () => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({
        top: rect.bottom + window.scrollY + 4,
        left: rect.right + window.scrollX - 160,
      });
    }
    setOpen(p => !p);
  };

  return (
    <div className="relative">
      <button ref={btnRef} onClick={handleOpen}
        className="p-1.5 rounded-lg text-on-surface-variant hover:bg-surface-container transition-colors">
        <span className="material-symbols-outlined text-[18px]">more_vert</span>
      </button>

      {open && (
        <>
          {/* ✅ Rendered in body via fixed positioning — escapes overflow */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="fixed z-50 bg-white rounded-xl shadow-xl border border-outline-variant/20 py-1 w-40 overflow-hidden"
            style={{ top: pos.top, left: pos.left }}
          >
            <button onClick={() => { setOpen(false); onBan(user); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold
                         text-amber-700 hover:bg-amber-50 transition-colors">
              <span className="material-symbols-outlined text-[16px]">block</span>Ban User
            </button>
            <div className="h-px bg-surface-container mx-2" />
            <button onClick={() => { setOpen(false); onDelete(user); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold
                         text-red-600 hover:bg-red-50 transition-colors">
              <span className="material-symbols-outlined text-[16px]">delete</span>Delete
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Main UsersPanel ───────────────────────────────────────
export default function UsersPanel({ search, setSearch }) {
  const [allUsers,  setAllUsers]  = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [modal,     setModal]     = useState(null);
  const [roleFilter, setRoleFilter] = useState('all');

  const { perPage, change: changePerPage } = usePerPage();
  const filteredUsers = roleFilter === 'all'
    ? allUsers
    : allUsers.filter(u => u.role === roleFilter);

  const pagination = usePagination(filteredUsers, perPage);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/admin/users', { params: { search } });
      setAllUsers(Array.isArray(data) ? data : data.users ?? []);
    } catch { }
    finally { setLoading(false); }
  }, [search]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { 
    fetchUsers();
    pagination.reset();
  }, [fetchUsers]);

  const handlePerPageChange = (n) => { changePerPage(n); pagination.reset(); };

  const handleRoleFilter = (r) => {
    setRoleFilter(r);
    pagination.reset();
  };

  const handleBan    = (u) => setModal({ user: u, action: 'ban' });
  const handleDelete = (u) => setModal({ user: u, action: 'delete' });

  const handleConfirm = async () => {
    const { user, action } = modal;
    setModal(null);
    try {
      if (action === 'delete') {
        await api.delete(`/api/admin/users/${user.id}`);
        setAllUsers(prev => prev.filter(u => u.id !== user.id));
      }
      if (action === 'ban') {
        await api.patch(`/api/admin/users/${user.id}/ban`);
        setAllUsers(prev => prev.map(u => u.id === user.id ? { ...u, is_banned: !u.is_banned } : u));
      }
    } catch { alert(`Could not ${action} user.`); }
  };

  const displayUsers = pagination.slice;

  return (
    <>
      {modal && (
        <ConfirmModal user={modal.user} action={modal.action}
          onConfirm={handleConfirm} onCancel={() => setModal(null)} />
      )}

      <div className="bg-white rounded-2xl shadow-sm border border-outline-variant/10 overflow-hidden">

        {/* Header */}
        <div className="p-6 border-b border-surface-container-high flex justify-between items-center gap-4 flex-wrap">
          <div>
            <h3 className="font-bold text-lg">All Users</h3>
            <p className="text-xs text-secondary mt-0.5">
              {filteredUsers.length} {roleFilter !== 'all' ? `${roleFilter}s` : 'total registered users'}
            </p>
          </div>
          <div className="flex items-center gap-2 bg-surface-container-low px-3 py-2
                          rounded-xl border border-outline-variant/20">
            <span className="material-symbols-outlined text-outline text-sm">search</span>
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, phone, email…"
              className="bg-transparent outline-none text-sm w-56 placeholder:text-outline-variant" />
            {search && (
              <button onClick={() => setSearch('')} className="text-outline hover:text-on-surface">
                <span className="material-symbols-outlined text-[16px]">close</span>
              </button>
            )}
          </div>
        </div>

        {/* Role pills */}
        <div className="px-6 py-3 flex gap-2 flex-wrap border-b border-surface-container-high bg-surface-container-low/50">
          {['all', 'farmer', 'customer', 'butcher', 'admin'].map((r) => {
            const count = r === 'all'
              ? allUsers.length
              : allUsers.filter(u => u.role === r).length;
            return (
              <button key={r}
                onClick={() => handleRoleFilter(r)}
                className={`px-3 py-1 rounded-full text-[11px] font-bold transition-all
                  ${roleFilter === r
                    ? r === 'all'
                      ? 'bg-gray-600 text-white'
                      : roleBadge(r) + ' ring-2 ring-offset-1 ring-current'
                    : r === 'all'
                      ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      : roleBadge(r) + ' opacity-60 hover:opacity-100'
                  }`}>
                {r === 'all' ? 'All' : r.charAt(0).toUpperCase() + r.slice(1)}
                <span className="ml-1 opacity-60">({count})</span>
              </button>
            );
          })}
        </div>

        {/* Table */}
        {loading ? (
          <div className="py-16 flex justify-center">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-surface-container">
              {displayUsers.map((u) => (
                <div key={u.id} className={`p-4 flex items-center gap-3 ${u.is_banned ? 'opacity-50' : ''}`}>
                  <div className="w-10 h-10 rounded-full bg-primary flex items-center
                                  justify-center text-white text-sm font-black flex-shrink-0">
                    {u.name[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-bold">{u.name}</p>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${roleBadge(u.role)}`}>{u.role}</span>
                      {u.is_banned && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-600">Banned</span>
                      )}
                    </div>
                    <p className="text-xs text-secondary font-mono">{u.phone}</p>
                    <p className="text-xs text-secondary truncate">{u.email}</p>
                    <p className="text-[11px] text-outline mt-0.5">
                      {u.city || '—'} · {new Date(u.created_at).toLocaleDateString('en-IN')}
                    </p>
                  </div>
                  <UserActions user={u} onBan={handleBan} onDelete={handleDelete} />
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto overflow-y-visible">
              <table className="w-full text-left">
                <thead className="bg-surface-container-low">
                  <tr>
                    {['ID', 'Name', 'Phone', 'Email', 'Role', 'City', 'Status', 'Joined', 'Actions'].map(h => (
                      <th key={h} className="px-5 py-3 text-[10px] font-black uppercase tracking-widest text-secondary whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-container">
                  {displayUsers.map((u) => (
                    <tr key={u.id} className={`hover:bg-surface-container-low/50 transition-colors ${u.is_banned ? 'opacity-50' : ''}`}>
                      <td className="px-5 py-3 text-xs text-secondary font-mono">{u.id}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-primary flex items-center
                                          justify-center text-white text-xs font-black flex-shrink-0">
                            {u.name[0]}
                          </div>
                          <span className="text-sm font-semibold whitespace-nowrap">{u.name}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-sm text-secondary font-mono">{u.phone}</td>
                      <td className="px-5 py-3 text-sm text-secondary max-w-[180px] truncate">{u.email}</td>
                      <td className="px-5 py-3">
                        <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${roleBadge(u.role)}`}>{u.role}</span>
                      </td>
                      <td className="px-5 py-3 text-sm text-secondary">{u.city || '—'}</td>
                      <td className="px-5 py-3">
                        <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold
                          ${u.is_banned ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-700'}`}>
                          {u.is_banned ? 'Banned' : 'Active'}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-xs text-secondary whitespace-nowrap">
                        {new Date(u.created_at).toLocaleDateString('en-IN')}
                      </td>
                      <td className="px-5 py-3 sticky right-0 bg-white">
                        <UserActions user={u} onBan={handleBan} onDelete={handleDelete} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Empty state */}
            {allUsers.length === 0 && (
              <div className="py-16 text-center text-secondary">
                <span className="material-symbols-outlined text-4xl text-outline-variant">person_off</span>
                <p className="mt-2 font-semibold">No users found</p>
                {search && <p className="text-xs mt-1">Try a different search term</p>}
              </div>
            )}

            {/* ── Footer: per-page + pagination ── */}
            {allUsers.length > 0 && (
              <div className="px-6 py-4 border-t border-surface-container-high space-y-3">
                <div className="flex items-center justify-between">
                  <PerPageSelector perPage={perPage} onChange={handlePerPageChange} />
                  <p className="text-xs text-secondary">
                    {((pagination.page - 1) * perPage) + 1}–{Math.min(pagination.page * perPage, allUsers.length)} of {allUsers.length}
                  </p>
                </div>
                <PaginationBar page={pagination.page} totalPages={pagination.totalPages} setPage={pagination.setPage} />
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}